import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Information } from "@/components/Information/Information";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetZohoCampaignLinkForListQuery,
	useGetZohoCampaignsQuery,
	useLinkZohoListToCampaignMutation,
	useUnlinkZohoListFromCampaignMutation,
} from "@/pages/Private/redux/zoho/zoho.api";

interface ZohoCampaignLinkSectionProps {
	isZohoEnabled: boolean;
	isConnected: boolean;
	listId: number;
}

export const ZohoCampaignLinkSection: FunctionComponent<ZohoCampaignLinkSectionProps> = ({
	isZohoEnabled,
	isConnected,
	listId,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.zoho.campaignLinks.${key}`);
	const tsSettings = (key: string) => t(`list.settings.zoho.campaignLink.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: campaignLink } = useGetZohoCampaignLinkForListQuery(listId, {
		skip: !isZohoEnabled || !isConnected || !listId,
	});
	const { data: campaigns } = useGetZohoCampaignsQuery(undefined, {
		skip: !isZohoEnabled || !isConnected,
	});
	const [linkToCampaign, { isLoading: isLinking }] = useLinkZohoListToCampaignMutation();
	const [unlinkFromCampaign, { isLoading: isUnlinking }] = useUnlinkZohoListFromCampaignMutation();

	const campaignOptions: DropdownAutoCompleteItem[] = useMemo(() => {
		if (!campaigns) {
			return [];
		}

		return campaigns.map((c) => ({
			id: c.id,
			title: c.name,
		}));
	}, [campaigns]);

	if (!isZohoEnabled || !isConnected) {
		return null;
	}

	const handleLink = async (value: DropdownAutoCompleteItem) => {
		try {
			await linkToCampaign({
				listId,
				zohoCampaignId: value.id as string,
				zohoCampaignName: value.title,
			}).unwrap();
			handleOpenSnackbar(ts("linkSuccess"));
		} catch {
			handleOpenSnackbar(ts("linkError"));
		}
	};

	const handleUnlink = async () => {
		try {
			await unlinkFromCampaign(listId).unwrap();
			handleOpenSnackbar(ts("unlinkSuccess"));
		} catch {
			handleOpenSnackbar(ts("unlinkError"));
		}
	};

	return (
		<div className="flex flex-col w-full mt-8">
			{renderMessageSnackbar()}
			<div className="flex flex-col gap-2 w-full mb-4">
				<div className="text-ssm font-medium text-text">
					{tsSettings("title")} <Information information={tsSettings("description")} />
				</div>
			</div>

			{campaignLink ? (
				<div className="flex flex-row items-center gap-4">
					<div className="text-ssm text-text">
						{ts("linked")}: <span className="font-medium">{campaignLink.zohoCampaignName}</span>
					</div>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={isUnlinking}
						isLoading={isUnlinking}
						onClick={handleUnlink}
						size={ButtonSize.S}
						title={ts("unlink")}
					/>
				</div>
			) : (
				<Dropdown
					data={campaignOptions}
					disabled={isLinking}
					floating={true}
					handleSelect={handleLink}
					label={ts("selectCampaign")}
				/>
			)}
		</div>
	);
};
